import { Button, cn, Input, type InputProps } from "@heroui/react";
import {
    safelyAppendSuffixToString,
    safelyRemoveSuffixFromString,
} from "@sr-partner-portal/common";
import { IconBulb, IconInfoCircle } from "@tabler/icons-react";
import {
    type ChangeEvent,
    type ReactNode,
    useCallback,
    useMemo,
    useState,
} from "react";
import {
    type FieldValues,
    type Path,
    type UseControllerProps,
    type UseControllerReturn,
    useController as useFieldController,
} from "react-hook-form";
import { Tooltip } from "@/base";
import { useFormControllerContext } from "@/contexts";
import type { AIContext } from "@/hooks/use-api-service/ai-api-service";
import { truncateToDecimals } from "@/utilities";
import { MagicWand, type MagicWandProps } from "../magic-wand";

type TextFieldProps<T extends FieldValues, AIContextKeys extends string> = {
    label: string;
    fieldName: UseControllerProps<T>["name"];
    placeholder?: string;
    description?: ReactNode;
    hint?: ReactNode;
    isAutofocusing?: boolean;
    autoComplete?: InputProps["autoComplete"];
    name?: InputProps["name"];
    isEnabled?: boolean;
    type?: InputProps["type"];
    isRequired?: boolean;
    startContent?: InputProps["startContent"];
    endContent?: InputProps["endContent"];
    endContentBeforeHint?: InputProps["endContent"];
    step?: InputProps["step"];
    min?: InputProps["min"];
    max?: InputProps["max"];
    maxLength?: number;
    shouldRegister?: boolean;
    suffix?: string;
    className?: string;
    classNames?: InputProps["classNames"];
    size?: InputProps["size"];
    fullWidth?: InputProps["fullWidth"];
    precision?: number;
    hideLabel?: boolean;
    ai?: Pick<
        MagicWandProps<AIContext<AIContextKeys>, string>,
        | "context"
        | "onCreate"
        | "onUpdate"
        | "showPopover"
        | "additionalInformationEnabled"
        | "additionalInformationRequired"
        | "additionalInformationLabel"
        | "additionalInformationHint"
        | "rotateResultsAfter"
    > & { uniqueId: string | number };
    isViewOnly?: boolean;
    "data-no-dnd"?: boolean;
    onChange?: (value: string) => void;
};

type BaseTextFieldProps<
    T extends FieldValues,
    AIContextKeys extends string = string,
> = TextFieldProps<T, AIContextKeys> & {
    fieldController?: UseControllerReturn<T, Path<T>>;
};

type RegisteredTextFieldProps<
    T extends FieldValues,
    AIContextKeys extends string = string,
> = Omit<TextFieldProps<T, AIContextKeys>, "shouldRegister">;

const formatValue = ({
    value: incomingValue,
    suffix,
    precision,
}: {
    value: string;
    suffix?: string;
    precision?: number;
}) => {
    let value = incomingValue;

    if (suffix && value && !value.endsWith(suffix)) {
        value = safelyAppendSuffixToString(value, suffix);
    }

    if (typeof precision === "number") {
        value = truncateToDecimals(value, precision);
    }

    return value;
};

const BaseTextField = <T extends FieldValues>({
    fieldController,
    description,
    hint,
    isAutofocusing = false,
    isEnabled = true,
    label,
    type,
    isRequired,
    startContent,
    endContent,
    endContentBeforeHint,
    suffix,
    step,
    min,
    max,
    className,
    classNames,
    size,
    precision,
    fieldName,
    placeholder,
    hideLabel,
    fullWidth = true,
    isViewOnly,
    maxLength,
    name,
    onChange,
    ...otherProps
}: BaseTextFieldProps<T>) => {
    const [isCopyTooltipOpen, setIsCopyTooltipOpen] = useState(false);
    const handleOnChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (!fieldController) {
                return;
            }

            const value = event.target.value;

            const formattedValue = formatValue({
                value,
                suffix,
                precision,
            });

            fieldController.field.onChange(formattedValue);
            onChange?.(formattedValue);
        },
        [fieldController, suffix, precision, onChange],
    );

    const value = useMemo(() => {
        let fieldValue = `${fieldController?.field.value ?? ""}`;

        if (suffix) {
            fieldValue = safelyRemoveSuffixFromString(fieldValue, suffix);
        }

        if (typeof precision === "number") {
            fieldValue = truncateToDecimals(fieldValue, precision);
        }

        return fieldValue;
    }, [precision, suffix, fieldController?.field]);

    const handleCopy = useCallback(async () => {
        try {
            const formattedValue = formatValue({
                value,
                suffix,
                precision,
            });

            await navigator.clipboard.writeText(formattedValue);
            setIsCopyTooltipOpen(true);
            setTimeout(() => setIsCopyTooltipOpen(false), 1000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    }, [value, precision, suffix]);

    return (
        <div className={cn("relative", className)}>
            <Input
                {...otherProps}
                name={name || fieldName}
                classNames={{
                    ...classNames,
                    inputWrapper: cn(
                        isViewOnly && "pointer-events-auto cursor-auto!",
                        classNames?.inputWrapper,
                    ),
                }}
                className={cn(isViewOnly && "opacity-70")}
                autoFocus={isAutofocusing}
                isDisabled={!isEnabled || isViewOnly}
                fullWidth={fullWidth}
                isInvalid={
                    fieldController ? !!fieldController.fieldState.error : false
                }
                errorMessage={fieldController?.fieldState.error?.message}
                label={hideLabel ? undefined : label}
                placeholder={placeholder}
                aria-label={label}
                onChange={handleOnChange}
                type={type}
                value={value}
                size={size}
                isRequired={isRequired}
                startContent={startContent}
                endContent={
                    <div className="flex h-full shrink-0 items-center gap-1">
                        {suffix}
                        {endContentBeforeHint}
                        {description && (
                            <Tooltip color="primary" content={description}>
                                <Button
                                    variant="light"
                                    isIconOnly={true}
                                    size="sm"
                                >
                                    <IconInfoCircle stroke={1.5} size={18} />
                                </Button>
                            </Tooltip>
                        )}
                        {hint && (
                            <Tooltip color="warning" content={hint}>
                                <Button
                                    variant="light"
                                    isIconOnly={true}
                                    size="sm"
                                >
                                    <IconBulb stroke={1.5} size={18} />
                                </Button>
                            </Tooltip>
                        )}
                        {endContent}
                    </div>
                }
                step={step}
                min={min}
                max={max}
            />
            {value && isViewOnly && (
                <Tooltip
                    content="Kopiert"
                    placement="top-end"
                    closeDelay={2000}
                    isOpen={isCopyTooltipOpen}
                    crossOffset={
                        description && hint ? 64 : description || hint ? 32 : 0
                    }
                >
                    <button
                        type="button"
                        className={cn(
                            "absolute top-0 left-0 z-10 h-full w-full",
                            (description || hint) && "w-[calc(100%-2rem)]",
                            description && hint && "w-[calc(100%-4rem)]",
                            description &&
                                endContentBeforeHint &&
                                "w-[calc(100%-4rem)]",
                        )}
                        /**
                         * The field stays a disabled single line while viewing,
                         * so a value which is too long to fit cannot be read by
                         * scrolling. Hovering shows it in full.
                         */
                        title={value}
                        onClick={handleCopy}
                    />
                </Tooltip>
            )}
        </div>
    );
};

const RegisteredTextField = <T extends FieldValues>({
    fieldName,
    endContent,
    ai,
    ...baseProps
}: RegisteredTextFieldProps<T>) => {
    const formController = useFormControllerContext();

    const fieldController = useFieldController<T>({
        control: formController.control,
        name: fieldName,
    });

    return (
        <BaseTextField
            {...baseProps}
            fieldName={fieldName}
            fieldController={fieldController}
            endContent={
                <>
                    {endContent}
                    {ai ? (
                        <MagicWand
                            {...ai}
                            id={`${ai.uniqueId}_${fieldName}`}
                            inputValue={fieldController.field.value}
                            onResult={fieldController.field.onChange}
                        />
                    ) : undefined}
                </>
            }
        />
    );
};

const TextField = <
    T extends FieldValues,
    AIContextKeys extends string = string,
>({
    shouldRegister = true,
    ...otherProps
}: TextFieldProps<T, AIContextKeys>) => {
    return shouldRegister ? (
        <RegisteredTextField {...otherProps} />
    ) : (
        <BaseTextField {...otherProps} />
    );
};

export { TextField };
